import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Layers, Gauge, Network, ArrowRight, Building2, GraduationCap,
  Globe, Telescope, BookOpen, FlaskConical
} from "lucide-react";

/**
 * White executive doctrinal expansion for /wait-theory.
 */

const LAYERS = [
  { id: 1, label: "Individual Intelligence",       desc: "Personal alignment, skills, trust, AI fluency" },
  { id: 2, label: "Team Intelligence",             desc: "Coordination, shared mental models, hybrid collaboration" },
  { id: 3, label: "Organizational Intelligence",   desc: "Strategy → Operations alignment, AI integration" },
  { id: 4, label: "Institutional Intelligence",    desc: "Governance, doctrine, credentialing reform" },
  { id: 5, label: "Societal-Global Intelligence",  desc: "Cross-system workforce resilience, AI-era equity" },
];

const FORCES = [
  { code: "HC",  label: "Human Capital",          color: "#1E3A8A" },
  { code: "TS",  label: "Technology & Systems",   color: "#0F766E" },
  { code: "LE",  label: "Leadership & Executive", color: "#C8A157" },
  { code: "OC",  label: "Organizational Culture", color: "#0F1E47" },
  { code: "OF",  label: "Operational Flow",       color: "#15803D" },
  { code: "SP",  label: "Strategic Posture",      color: "#991B1B" },
  { code: "AIR", label: "AI Readiness",           color: "#7C3AED" },
];

const ECO_NODES = [
  { icon: BookOpen,     label: "SCFR-Journal",        to: "/scfr-journal" },
  { icon: GraduationCap,label: "Programs",            to: "/programs" },
  { icon: FlaskConical, label: "Zodiac LMS-Lab",      to: "/ecosystem/zodiac-lms-lab" },
  { icon: Telescope,    label: "Observatory",         to: "/observatories/global" },
  { icon: Globe,        label: "Partnership Network", to: "/ecosystem/partnership" },
  { icon: Building2,    label: "JusWork",             to: "/juswork" },
];

function SectionHeader({ accent, eyebrow, title, accentTitle, sub }) {
  return (
    <div className="text-center mb-12">
      <div className="zodiac-eyebrow mb-3 inline-flex items-center gap-3" style={{ color: accent }}>
        <span className="w-8 h-px" style={{ background: accent }} />
        {eyebrow}
        <span className="w-8 h-px" style={{ background: accent }} />
      </div>
      <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] leading-tight">
        {title} <span className="italic text-[#991B1B] font-normal">{accentTitle}</span>
      </h2>
      {sub && <p className="text-[#475569] max-w-2xl mx-auto mt-4 leading-relaxed">{sub}</p>}
    </div>
  );
}

function FiveLayerArchitecture() {
  return (
    <section className="bg-white py-20" data-testid="five-layer-architecture">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          accent="#1E3A8A"
          eyebrow="Five-Layer Architecture"
          title="Workforce intelligence at"
          accentTitle="every layer."
          sub="WAIT Theory models workforce alignment as a stack — from the individual up to society — where each layer carries diagnostic, developmental, and transformational implications."
        />
        <div className="space-y-3">
          {LAYERS.slice().reverse().map((layer, i) => (
            <motion.div
              key={layer.id}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              viewport={{ once: true }}
              className="zodiac-hairline-card flex items-center gap-5 p-5"
            >
              <div className="w-14 h-14 rounded-lg bg-[#C8A157]/15 border border-[#C8A157]/40 flex items-center justify-center font-heading font-bold text-[#7B5A1A] text-xl flex-shrink-0">
                L{layer.id}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47] mb-1">{layer.label}</h3>
                <p className="text-[#6B7280] text-sm">{layer.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WIISummary() {
  return (
    <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20" data-testid="wii-summary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
        >
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3" style={{ color: "#0F766E" }}>
            <span className="w-8 h-px bg-[#0F766E]" />
            Workforce Intelligence Index
          </div>
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] mb-4 leading-tight">
            One composite. <span className="italic text-[#991B1B] font-normal">Five dimensions.</span>
          </h2>
          <p className="text-[#475569] leading-relaxed mb-6">
            The Workforce Intelligence Index (WII) is the diagnostic instrument of WAIT
            Theory — a single composite score derived from five validated dimensions of
            workforce alignment. The WII enables institutional benchmarking, longitudinal
            tracking, and Human-AI era readiness assessment.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/observatories/global/survey">
              <button className="h-11 px-5 rounded-md bg-[#0F766E] hover:bg-[#0E5F58] text-white font-semibold inline-flex items-center gap-2">
                Take the Diagnostic <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/observatories/global/dashboard">
              <button className="h-11 px-5 rounded-md border border-[#0F1E47]/15 text-[#0F1E47] hover:bg-white">
                View Global Dashboard
              </button>
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="relative aspect-square max-w-md mx-auto"
        >
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <defs>
              <linearGradient id="wii-arc-white" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#0F766E" />
                <stop offset="1" stopColor="#1E3A8A" />
              </linearGradient>
            </defs>
            <circle cx="100" cy="100" r="80" stroke="#E5E7EB" strokeWidth="14" fill="none" />
            <circle
              cx="100" cy="100" r="80"
              stroke="url(#wii-arc-white)"
              strokeWidth="14"
              fill="none"
              strokeDasharray="503"
              strokeDashoffset="125"
              strokeLinecap="round"
              transform="rotate(-90 100 100)"
            />
            <text x="100" y="92" textAnchor="middle" fill="#0F1E47" fontSize="40" fontWeight="700" fontFamily="Playfair Display, serif">
              7.5
            </text>
            <text x="100" y="118" textAnchor="middle" fill="#6B7280" fontSize="10" letterSpacing="2">
              WII · STRONG &amp; ALIGNED
            </text>
          </svg>
          <div className="grid grid-cols-5 gap-2 mt-4 text-center">
            {["Trust","Alignment","Readiness","AI Capability","Resilience"].map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-wider text-[#6B7280]">{d}</div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SevenForcesDiagram() {
  return (
    <section className="bg-white py-20 overflow-hidden" data-testid="seven-forces-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          accent="#C8A157"
          eyebrow="Seven Forces of Workforce Alignment"
          title="The forces that converge into"
          accentTitle="intelligence."
          sub="Seven institutional forces continuously converge to determine alignment. WAIT Theory measures their balance, friction, and convergence pattern."
        />
        <div className="relative w-full max-w-2xl mx-auto aspect-square scale-[0.78] sm:scale-100 origin-center">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#C8A157] to-[#9F7B33] border-4 border-white shadow-[0_8px_30px_rgba(200,161,87,0.4)] flex items-center justify-center">
              <div className="text-center">
                <div className="font-heading text-2xl font-bold text-white">WII</div>
                <div className="text-[9px] uppercase tracking-widest text-white/85 mt-1">Convergence</div>
              </div>
            </div>
          </div>
          {FORCES.map((f, i) => {
            const angle = (i / FORCES.length) * Math.PI * 2 - Math.PI / 2;
            const r = 42;
            const x = 50 + r * Math.cos(angle);
            const y = 50 + r * Math.sin(angle);
            return (
              <motion.div
                key={f.code}
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.07 }}
                viewport={{ once: true }}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${x}%`, top: `${y}%` }}
              >
                <div
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-white flex flex-col items-center justify-center text-white shadow-xl"
                  style={{ backgroundColor: f.color }}
                >
                  <div className="font-heading font-bold text-lg">{f.code}</div>
                  <div className="text-[9px] uppercase tracking-wider text-center px-1 leading-tight opacity-90">
                    {f.label.split(" & ")[0]}
                  </div>
                </div>
              </motion.div>
            );
          })}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            {FORCES.map((_, i) => {
              const a = (i / FORCES.length) * Math.PI * 2 - Math.PI / 2;
              const x = 50 + 38 * Math.cos(a);
              const y = 50 + 38 * Math.sin(a);
              return <line key={i} x1="50" y1="50" x2={x} y2={y} stroke="#E5E7EB" strokeWidth="0.5" />;
            })}
          </svg>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mt-12">
          {FORCES.map((f) => (
            <div key={f.code} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: f.color }} />
              <span className="text-[#475569]">{f.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EcosystemMap() {
  return (
    <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20" data-testid="wait-ecosystem-map">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          accent="#7C3AED"
          eyebrow="Doctrine in Action"
          title="WAIT Theory across the"
          accentTitle="Zodiac ecosystem."
          sub="The doctrine doesn't live in isolation. It drives every node of the institutional ecosystem."
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {ECO_NODES.map((n, i) => (
            <motion.div
              key={n.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              viewport={{ once: true }}
            >
              <Link
                to={n.to}
                className="zodiac-hairline-card group flex flex-col items-center gap-3 p-5"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C8A157]/20 to-[#1E3A8A]/10 border border-[#E5E7EB] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <n.icon className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <span className="text-[#0F1E47] text-sm text-center font-medium">{n.label}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function WAITAdditionalDoctrine() {
  return (
    <>
      <FiveLayerArchitecture />
      <WIISummary />
      <SevenForcesDiagram />
      <EcosystemMap />
    </>
  );
}
